// src/components/Dashboard/components/StockChart.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Settings, X } from 'lucide-react';

const StockChart = ({
  data,
  indicators = {},
  currentIndicators = [],
  onIndicatorsChange
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // ✅ FIX: Handle the THREE-LEVEL nesting correctly
  const { chartData, indicatorData } = useMemo(() => {

    
    if (!data) {
      //console.log("[StockChart] No data provided");
      return { chartData: [], indicatorData: {} };
    }

    let normalized = [];
    let indicators = {};

    // ✅ CRITICAL: Handle THREE levels of nesting from Redux
    // state.stockData.data.data[0] <- THREE .data references
    if (data.data && typeof data.data === 'object') {
      // Check if data.data.data exists (array of price points)
      if (data.data.data && Array.isArray(data.data.data)) {
        normalized = data.data.data;

      }
      // Fallback: Check if data.data is directly an array
      else if (Array.isArray(data.data)) {
        normalized = data.data;
     
      }
      
      // Get indicators from data.indicators or data.data.indicators
      indicators = data.indicators || data.data.indicators || {};
    }
    // Fallback: Direct array
    else if (Array.isArray(data)) {
      normalized = data;
    
    }


    return {
      chartData: normalized,
      indicatorData: indicators
    };
  }, [data]);

  // Available indicators
  const availableIndicators = [
    { id: 'sma', name: 'SMA (20)', color: '#3b82f6' },
    { id: 'ema', name: 'EMA', color: '#8b5cf6' },
    { id: 'macd', name: 'MACD', color: '#ec4899' },
    { id: 'rsi', name: 'RSI', color: '#10b981' },
    { id: 'bollinger_bands', name: 'Bollinger', color: '#f59e0b' }
  ];

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const width = rect.width;
      const height = Math.max(rect.height, 260);

      setDimensions({ width, height });
    };

    updateDimensions();
    
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, 150);
    };

    window.addEventListener("resize", debouncedResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, []);

  // ✅ Draw chart with ALL dependencies
  useEffect(() => {
    if (!canvasRef.current || chartData.length === 0 || !dimensions.width) {
      /*console.log("[StockChart] Skipping draw:", {
        hasCanvas: !!canvasRef.current,
        dataLength: chartData.length,
        width: dimensions.width
      });*/
      return;
    }


    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;

    // Set canvas size with device pixel ratio
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // ✅ Extract prices with ALL possible property names
    const prices = chartData.map(d => {
      const price = 
        d.period_close ??
        d.avg_close ??
        d.close ??
        d.c ??
        d.price ??
        0;
      return Number(price);
    }).filter(p => !isNaN(p) && isFinite(p));

    // Extract timestamps
    const timestamps = chartData.map(d => {
      if (d.start_date) return d.start_date;
      if (d.end_date) return d.end_date;
      if (d.period) {
        const year = d.period.substring(0, 4);
        const month = d.period.substring(4, 6);
        const day = d.period.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
      if (d.date) return d.date;
      return null;
    });

    if (prices.length === 0) {
      console.warn("[StockChart]  No valid prices extracted from data");
      return;
    }


    // Calculate chart area
    const padding = { top: 20, right: 60, bottom: 40, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Find min/max for scaling
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;

    const scaleY = (price) => {
      return padding.top + chartHeight - ((price - minPrice) / priceRange) * chartHeight;
    };

    const scaleX = (index) => {
      const denom = Math.max(prices.length - 1, 1);
      return padding.left + (index / denom) * chartWidth;
    };

    // Draw grid
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartHeight / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      const price = maxPrice - (priceRange / 4) * i;
      ctx.fillStyle = 'rgba(148, 163, 184, 0.6)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`$${price.toFixed(2)}`, width - padding.right + 5, y + 4);
    }

    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const isPositive = lastPrice >= firstPrice;

    // Draw gradient fill
    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    if (isPositive) {
      gradient.addColorStop(0, 'rgba(34, 197, 94, 0.2)');
      gradient.addColorStop(1, 'rgba(34, 197, 94, 0.0)');
    } else {
      gradient.addColorStop(0, 'rgba(239, 68, 68, 0.2)');
      gradient.addColorStop(1, 'rgba(239, 68, 68, 0.0)');
    }

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(scaleX(0), height - padding.bottom);
    prices.forEach((price, i) => {
      ctx.lineTo(scaleX(i), scaleY(price));
    });
    ctx.lineTo(scaleX(prices.length - 1), height - padding.bottom);
    ctx.closePath();
    ctx.fill();

    // Draw price line
    ctx.strokeStyle = isPositive ? '#22c55e' : '#ef4444';
    ctx.lineWidth = 2;
    ctx.beginPath();
    prices.forEach((price, i) => {
      const x = scaleX(i);
      const y = scaleY(price);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw indicators
    if (currentIndicators.length > 0 && indicatorData) {
      //console.log("[StockChart] Drawing indicators:", currentIndicators, indicatorData);
      
      currentIndicators.forEach(indicatorId => {
        const indicator = indicatorData[indicatorId];
        
        if (!indicator) {
          console.warn(`[StockChart] No data for indicator: ${indicatorId}`);
          return;
        }

        let values = [];
        if (Array.isArray(indicator)) {
          values = indicator;
        } else if (indicator.values && Array.isArray(indicator.values)) {
          values = indicator.values;
        } else if (indicator.data && Array.isArray(indicator.data)) {
          values = indicator.data;
        }

        if (values.length === 0) {
          console.warn(`[StockChart] No values for indicator: ${indicatorId}`);
          return;
        }

        const indicatorConfig = availableIndicators.find(a => a.id === indicatorId);
        if (!indicatorConfig) return;

        ctx.strokeStyle = indicatorConfig.color;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        
        let hasDrawn = false;
        values.forEach((value, i) => {
          if (value !== null && value !== undefined && !isNaN(value) && isFinite(value)) {
            const x = scaleX(i);
            const y = scaleY(Number(value));
            
            if (!hasDrawn) {
              ctx.moveTo(x, y);
              hasDrawn = true;
            } else {
              ctx.lineTo(x, y);
            }
          }
        });
        
        if (hasDrawn) {
          ctx.stroke();
        }
        ctx.setLineDash([]);
      });
    }

    // Draw hover point
    if (hoveredPoint !== null && hoveredPoint < prices.length) {
      const x = scaleX(hoveredPoint);
      const y = scaleY(prices[hoveredPoint]);

      ctx.strokeStyle = 'rgba(148, 163, 184, 0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, height - padding.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = isPositive ? '#22c55e' : '#ef4444';
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();

      const tooltipWidth = 120;
      const tooltipHeight = 60;
      const tooltipX = x > width / 2 ? x - tooltipWidth - 10 : x + 10;
      const tooltipY = Math.max(padding.top, Math.min(y - tooltipHeight / 2, height - padding.bottom - tooltipHeight));

      ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)';
      ctx.lineWidth = 1;
      roundRect(ctx, tooltipX, tooltipY, tooltipWidth, tooltipHeight, 6);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#f1f5f9';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`$${prices[hoveredPoint].toFixed(2)}`, tooltipX + 10, tooltipY + 22);

      ctx.font = '11px sans-serif';
      ctx.fillStyle = '#94a3b8';
      if (timestamps[hoveredPoint]) {
        const date = new Date(timestamps[hoveredPoint]);
        ctx.fillText(date.toLocaleDateString(), tooltipX + 10, tooltipY + 42);
      }
    }

  }, [chartData, indicatorData, dimensions, currentIndicators, hoveredPoint]);

  const roundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current || chartData.length === 0) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padding = { left: 10, right: 60 };
    const chartWidth = dimensions.width - padding.left - padding.right;
    
    if (x < padding.left || x > dimensions.width - padding.right) {
      setHoveredPoint(null);
      return;
    }

    const index = Math.round(((x - padding.left) / chartWidth) * (chartData.length - 1));
    setHoveredPoint(Math.max(0, Math.min(index, chartData.length - 1)));
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  const toggleIndicator = (indicatorId) => {
    if (!onIndicatorsChange) return;
    
    const newIndicators = currentIndicators.includes(indicatorId)
      ? currentIndicators.filter(id => id !== indicatorId)
      : [...currentIndicators, indicatorId];
    
    onIndicatorsChange(newIndicators);
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-cream-100 dark:bg-slate-900/40 rounded">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-2"></div>
          <p className="text-xs text-gray-500 dark:text-slate-400">{t('dashboard.stockChart.loadingChartData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        ref={containerRef} 
        className="relative w-full h-64 bg-cream-100 dark:bg-slate-900/40 rounded overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="cursor-crosshair"
        />
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {currentIndicators.map(id => {
            const indicator = availableIndicators.find(a => a.id === id);
            if (!indicator) return null;
            return (
              <div
                key={id}
                className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-slate-800/60 rounded text-xs"
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: indicator.color }}
                />
                <span className="text-gray-600 dark:text-slate-300">{indicator.name}</span>
                <button
                  onClick={() => toggleIndicator(id)}
                  className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowIndicatorMenu(!showIndicatorMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800 border border-gray-300 dark:border-slate-700/40 rounded text-xs text-gray-600 dark:text-slate-300 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            <span>{t('dashboard.stockChart.indicators')}</span>
          </button>

          {showIndicatorMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-10">
              <div className="p-2 space-y-1">
                {availableIndicators.map(indicator => (
                  <button
                    key={indicator.id}
                    onClick={() => {
                      toggleIndicator(indicator.id);
                      setShowIndicatorMenu(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded text-xs transition-colors ${
                      currentIndicators.includes(indicator.id)
                        ? 'bg-blue-600/20 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: indicator.color }}
                    />
                    <span className="flex-1 text-left">{indicator.name}</span>
                    {currentIndicators.includes(indicator.id) && (
                      <span className="text-blue-600 dark:text-blue-400">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockChart;